import i18n from '@/libs/i18n'
import store from '@/store'
import { nomSearch, nomSearchStructured, nomReverse } from '@/services/nominatim/nominatimClient'
import { detectPlatform } from '@/utils/platform'
import { normalizeAddress } from '@/utils/location/normalizeAddress'
import { normalizeCoords } from '@/utils/location/geoDistance'

const FORMAT = 'jsonv2'

// decide language per request
export function pickLang(countryCode) {
  const fromStore = store && store.state && store.state.user && store.state.user.geoData && store.state.user.geoData.country_code
  if (fromStore) return String(fromStore).toLowerCase()
  if (countryCode) return String(countryCode).toLowerCase()
  return 'es'
}

function toCountrycodes(cc) {
  return cc ? String(cc).toLowerCase() : undefined
}

export function extractGeoDataFromResponse(response) {
  return (
    response?.data?.data
    || response?.data
    || response
    || null
  )
}

export function normalizeGeoData(geoData) {
  if (!geoData || typeof geoData !== 'object') return null

  const coords = normalizeCoords(geoData)

  if (!coords) return null

  const rawAddress = geoData.address || {}
  const normalizedAddress = normalizeAddress(rawAddress)

  const address = {
    ...rawAddress,
    ...normalizedAddress,
  }

  const countryCode = geoData.country_code || address.country_code

  return {
    ...geoData,
    lat: coords.lat,
    lon: coords.lng,
    address,
    ...(countryCode ? { country_code: String(countryCode).toUpperCase() } : {}),
  }
}

export function createInvalidGeoDataError() {
  const error = new Error(i18n.t('location.errors.GeolocationUnavailable'))

  error.code = 'GEOLOCATION_DATA_MISSING'

  return error
}

export function requireGeoData(geoData) {
  const normalizedGeoData = normalizeGeoData(geoData)

  if (!normalizedGeoData) {
    throw createInvalidGeoDataError()
  }

  return normalizedGeoData
}

export function requireGeoDataFromResponse(response) {
  return requireGeoData(extractGeoDataFromResponse(response))
}

export function normalizeAddressPartForCompare(value) {
  return String(value || '')
    .trim()
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[.,;:()[\]{}'"`’‘“”\-_/\\]+/g, ' ')
    .replace(/\s+/g, ' ')
}

export function isSameAddressPart(a, b) {
  const left = normalizeAddressPartForCompare(a)
  const right = normalizeAddressPartForCompare(b)

  return !!left && !!right && left === right
}

export function uniqueAddressParts(parts) {
  const used = new Set()

  return parts
    .map(part => String(part || '').trim())
    .filter(Boolean)
    .filter(part => {
      const key = normalizeAddressPartForCompare(part)

      if (!key || used.has(key)) return false

      used.add(key)

      return true
    })
}

export function firstAddressPart(...parts) {
  return parts.find(part => String(part || '').trim()) || ''
}

export function getAddressLocality(address = {}) {
  return firstAddressPart(
    address.locality,
    address.city,
    address.town,
    address.village,
    address.municipality,
  )
}

export function getAddressRegion(address = {}, locality = '') {
  const candidates = [
    address.province,
    address.state,
    address.state_district,
    address.county,
    address.region,
  ]

  return candidates.find(part => part && !isSameAddressPart(part, locality)) || ''
}

export function getFormattedAddress(location, optionsOrApartmentNumber = {}, legacyComment = false) {
  const options = typeof optionsOrApartmentNumber === 'object' && optionsOrApartmentNumber !== null
    ? optionsOrApartmentNumber
    : {
      apartmentNumber: optionsOrApartmentNumber,
      comment: legacyComment,
    }

  const {
    apartmentNumber = true,
    comment = false,
    exclude = [],
    includeDistrict = true,
    includePostcode = true,
    uppercaseCountry = false,
  } = options

  if (!location) return ''

  const address = { ...location }

  if (!apartmentNumber) delete address.apartment_number

  const excluded = Array.isArray(exclude) ? exclude : []

  const isAllowed = key => !excluded.includes(key)

  const locality = getAddressLocality(address)
  const region = getAddressRegion(address, locality)

  const district = firstAddressPart(
    address.borough,
    address.city_district,
    address.district,
    address.suburb,
    address.neighbourhood,
  )

  const country = firstAddressPart(address.country)
  const normalizedCountry = country && uppercaseCountry
    ? country.toUpperCase()
    : country

  const parts = [
    isAllowed('road') ? address.road : '',
    isAllowed('building') ? address.building : '',
    isAllowed('house_number') ? address.house_number : '',
    isAllowed('apartment_number') ? address.apartment_number : '',
    includeDistrict && isAllowed('district') && !isSameAddressPart(district, locality) ? district : '',
    isAllowed('locality') ? locality : '',
    isAllowed('state') ? region : '',
    includePostcode && isAllowed('postcode') ? address.postcode : '',
    isAllowed('country') ? normalizedCountry : '',
  ]

  const baseAddress = uniqueAddressParts(parts).join(', ')

  if (address.address_comment && comment) {
    return `${baseAddress} (${address.address_comment})`
  }

  return baseAddress
}
// --- Helpers ---------------------------------------------------------------

/** Small sleep helper */
function delay(ms) {
  return new Promise(r => setTimeout(r, ms))
}

/** Try to read the geolocation permission state; returns 'granted' | 'prompt' | 'denied' | null */
export async function getGeoPermissionState() {
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const res = await navigator.permissions.query({ name: 'geolocation' })
      return res && res.state || null
    }
  } catch (_) {}
  return null
}

export function getGeolocationDeniedMeta() {

  const platform = detectPlatform()

  if (platform.isAndroidWebView) {
    return {
      code: 'ANDROID_WEBVIEW_GEOLOCATION_DENIED',
      message: i18n.t('location.errors.GeolocationDenied'),
      instruction: i18n.t('location.instructions.android_webview_location_permission'),
    }
  }

  if (platform.isAndroid) {
    return {
      code: 'ANDROID_GEOLOCATION_DENIED',
      message: i18n.t('location.errors.GeolocationDenied'),
      instruction: i18n.t('location.instructions.android_browser_location_permission'),
    }
  }

  if (platform.isDesktop) {
    return {
      code: 'DESKTOP_GEOLOCATION_DENIED',
      message: i18n.t('location.errors.GeolocationDenied'),
      instruction: i18n.t('location.instructions.desktop_location_permission'),
    }
  }

  return {
    code: 'GEOLOCATION_DENIED',
    message: i18n.t('location.errors.GeolocationDenied'),
    instruction: i18n.t('location.instructions.browser_location_permission'),
  }
}

export function createGeolocationDeniedError() {
  const meta = getGeolocationDeniedMeta()
  const error = new Error(meta.message)

  error.code = meta.code
  error.instruction = meta.instruction

  return error
}

export function getGeolocationPermissionDeniedMessage() {
  const platform = detectPlatform()

  if (platform.isAndroidWebView) {
    return i18n.t('location.errors.AndroidWebViewGeolocationDenied')
  }

  if (platform.isAndroid) {
    return i18n.t('location.errors.AndroidGeolocationDenied')
  }

  return i18n.t('location.errors.GeolocationDenied')
}

/** One-shot geolocation with sensible defaults */
function getCurrentPositionOnce({
  timeout = 15000,
  enableHighAccuracy = true,
  maximumAge = 0,
} = {}) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(Object.assign(new Error(i18n.t('location.errors.GeolocationUnsupported')), { code: -1 }))
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve(coords),
      err => reject(err),
      { timeout, enableHighAccuracy, maximumAge },
    )
  })
}

// --- Public API ------------------------------------------------------------
/**
 * Get current coordinates robustly (Android-friendly).
 * Strategy:
 *  - Preflight permission and short-circuit if denied.
 *  - Phase A: quick low-accuracy call (fast / often avoids accuracy sheet).
 *    If it fails and we lost focus (Android sheet), wait for focus/visibility.
 *  - Phase B: warm-up via watchPosition (often faster on Android).
 *  - Phase C: final high-accuracy getCurrentPosition.
 *  - Normalize/throw GeolocationDenied on denial.
 */
export async function currentCoordinates(options = {}) {
  const state = await getGeoPermissionState()
  if (state === 'denied') {
    const e = createGeolocationDeniedError()
    e.geolocationCode = -2
    throw e
  }

  // --- Phase A: quick low-accuracy (uses cache; triggers prompt fast)
  try {
    const coords = await getCurrentPositionOnce({
      enableHighAccuracy: false,
      timeout: 5000,
      maximumAge: 2 * 60 * 1000,
      ...options,
    })
    return coords
  } catch (e) {
    // If the Android accuracy sheet appeared, page may blur or visibility may change
    await waitForFocusOrVisibility(20000)
  }

  // --- Phase B: warm-up via watchPosition (still low-latency but can be high-accuracy)
  try {
    const warmed = await warmUpWithWatchPosition(8000, true)
    return warmed
  } catch (_) {
    // ignore; proceed to final attempt
  }

  // --- Phase C: final high-accuracy, generous timeout, no cache
  try {
    const fresh = await getCurrentPositionOnce({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
      ...options,
    })
    return fresh
  } catch (err) {
    // Normalize denial if possible
    if (err && (err.code === 1 || err.message === 'GeolocationDenied')) {
      const e = createGeolocationDeniedError()
      e.geolocationCode = 1
      throw e
    }
    // Make transient codes obvious to callers: 2 (POSITION_UNAVAILABLE), 3 (TIMEOUT)
    const ex = new Error(i18n.t('location.errors.GeolocationUnavailable'))
    ex.code = err && err.code
    throw ex
  }
}

/**
 * Kept for compatibility: returns the params that will be sent to Nominatim.
 * Useful if other parts of the app inspect/query parameters.
 */
export function getSearchQueryParams(address, countryCode, structured) {
  const lang = pickLang(countryCode)
  const cc = toCountrycodes(countryCode)

  if (structured) {
    const params = {
      format: FORMAT,
      addressdetails: 1,
      countrycodes: cc,
      lang,
      state: address.state,
      country: address.country,
    }
    if (address.locality) params.city = address.locality
    if (address.postcode) params.postalcode = address.postcode
    if (address.house_number && address.road) params.street = `${address.house_number} ${address.road}`
    else if (address.road) params.street = `${address.road}`
    return params
  }

  return {
    format: FORMAT,
    q: address,
    addressdetails: 1,
    countrycodes: cc,
    lang,
  }
}

export async function locationBySearchQuery(address, countryCode, structured) {
  const lang = pickLang(countryCode)
  const countrycodes = toCountrycodes(countryCode)

  if (structured && address && typeof address === 'object') {
    // structured search
    return nomSearchStructured(
      {
        house_number: address.house_number,
        road: address.road,
        city: address.city || address.locality,
        locality: address.locality,
        state: address.state,
        postcode: address.postcode,
        country: address.country,
      },
      {
        countrycodes, lang, addressdetails: 1, limit: 10,
      },
    )
  }

  // free-text search
  return nomSearch(address, {
    countrycodes, lang, addressdetails: 1, limit: 10,
  })
}

export async function locationByCoordinates({
  latitude, longitude, lat, lon, lng,
}, countryCode) {
  const lang = pickLang(countryCode)
  const countrycodes = toCountrycodes(countryCode)
  const coords = normalizeCoords({
    lat: lat ?? latitude,
    lng: lng ?? lon ?? longitude,
  })

  if (!coords) {
    throw createInvalidGeoDataError()
  }

  const geoData = await nomReverse(
    { lat: coords.lat, lon: coords.lng },
    { countrycodes, lang, addressdetails: 1 },
  )

  return requireGeoData(geoData)
}

export async function currentLocation(countryCode, options = {}) {
  const coords = await currentCoordinates(options)
  return locationByCoordinates(coords, countryCode)
}

// helper: compute bbox around lat/lng using radius in KM
export function computeViewport(lat, lng, radiusKm) {
  const toRad = d => d * Math.PI / 180
  const cos = Math.cos(toRad(lat)) || 1e-9
  const dLat = radiusKm / 111.32
  const dLng = radiusKm / (111.32 * cos)
  return {
    north_lat: lat + dLat,
    south_lat: lat - dLat,
    east_lng: lng + dLng,
    west_lng: lng - dLng,
  }
}
/**
 * Convert meters to a compact "km : m" label.
 * Always returns a string and is resilient to null/NaN/negative inputs.
 *
 * @param {number|null|undefined} m
 * @returns {string}
 */
export function metersToLabel(m) {
  const val = Number.isFinite(m) ? Math.max(0, Math.floor(m)) : 0
  const nf = opts => new Intl.NumberFormat(i18n.locale, opts)

  if (val === 0) {
    const n = nf().format(0)
    return i18n.t('distance.km', { n })
  }

  if (val >= 1000) {
    const kmExact = val / 1000
    const kmRounded = Math.round(kmExact * 10) / 10
    const n = nf({
      minimumFractionDigits: Number.isInteger(kmRounded) ? 0 : 1,
      maximumFractionDigits: 1,
    }).format(kmRounded)
    return i18n.t('distance.km', { n })
  }

  const n = nf().format(val)
  return i18n.t('distance.m', { n })
}
// --- local parser moved here from AddLocationWizard.vue ---
function dmsToDec(d, m, s, h) {
  const v = Number(d) + Number(m) / 60 + Number(s) / 3600
  return (h === 'S' || h === 'W') ? -v : v
}
export function parsePastedCoordinates(str) {
  const s = String(str || '').trim()
  // 1) Google long URL with @lat,lng
  let m = s.match(/@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 2) q=lat,lng
  m = s.match(/[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 3) ll=lat,lng
  m = s.match(/[?&]ll=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 4) data=!3dLAT!4dLNG
  m = s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/i)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 5) raw "lat,lng"
  m = s.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 6) "lat: X lng: Y"
  m = s.match(/lat(?:itude)?\s*[:=]\s*(-?\d+(?:\.\d+)?)[^\d-]+(?:lng|lon|long(?:itude)?)\s*[:=]\s*(-?\d+(?:\.\d+)?)/i)
  if (m) return { lat: +m[1], lng: +m[2] }
  // 7) DMS
  m = s.match(/(\d{1,3})[^\d]+(\d{1,2})[^\d]+(\d{1,2}(?:\.\d+)?)\s*["']?\s*([NS])[^0-9]+(\d{1,3})[^\d]+(\d{1,2})[^\d]+(\d{1,2}(?:\.\d+)?)\s*["']?\s*([EW])/i)
  if (m) return { lat: dmsToDec(+m[1], +m[2], +m[3], m[4].toUpperCase()), lng: dmsToDec(+m[5], +m[6], +m[7], m[8].toUpperCase()) }
  return null
}
/**
 * Normalize various viewport shapes into a Leaflet-like bounds object:
 *   - [[south, west], [north, east]]  (array form)
 *   - { south_lat, west_lng, north_lat, east_lng }  (API form)
 *   - { _southWest:{lat,lng}, _northEast:{lat,lng} } (already Leaflet-like)
 *
 * Returns {} when input is invalid.
 */
export function toLeafletBounds(input) {
  // Already Leaflet-like?
  if (input && input._southWest && input._northEast) {
    const s = Number(input._southWest.lat)
    const w = Number(input._southWest.lng)
    const n = Number(input._northEast.lat)
    const e = Number(input._northEast.lng)
    if ([s, w, n, e].every(Number.isFinite)) {
      return {
        _southWest: { lat: s, lng: w },
        _northEast: { lat: n, lng: e },
      }
    }
    return {}
  }

  // API-style object { south_lat, west_lng, north_lat, east_lng }
  if (input && typeof input === 'object'
    && ('south_lat' in input || 'west_lng' in input || 'north_lat' in input || 'east_lng' in input)) {
    const s = Number(input.south_lat)
    const w = Number(input.west_lng)
    const n = Number(input.north_lat)
    const e = Number(input.east_lng)
    if ([s, w, n, e].every(Number.isFinite)) {
      return {
        _southWest: { lat: s, lng: w },
        _northEast: { lat: n, lng: e },
      }
    }
    return {}
  }

  // Array form [[south, west], [north, east]]
  if (Array.isArray(input) && input.length === 2
    && Array.isArray(input[0]) && Array.isArray(input[1])) {
    const s = Number(input[0][0])
    const w = Number(input[0][1])
    const n = Number(input[1][0])
    const e = Number(input[1][1])
    if ([s, w, n, e].every(Number.isFinite)) {
      return {
        _southWest: { lat: s, lng: w },
        _northEast: { lat: n, lng: e },
      }
    }
    return {}
  }

  return {}
}
export const RADIUS_MIN = 100 // meters
export const RADIUS_MAX = 500 // meters

export function clampRadius(v, min = RADIUS_MIN, max = RADIUS_MAX) {
  const n = Number(v) || 0
  return Math.max(min, Math.min(max, n))
}
// --- Privacy-friendly geocoordinate helpers -------------------------------

/**
 * Rough meters per 1° latitude.
 * @returns {number}
 */
export function metersPerDegreeLat() { return 111320 }

/**
 * Rough meters per 1° longitude at a given latitude.
 * @param {number} latDeg
 * @returns {number}
 */
export function metersPerDegreeLng(latDeg) {
  const lat = Number.isFinite(latDeg) ? latDeg : 0
  return 111320 * Math.cos((lat * Math.PI) / 180)
}

/**
 * Snap coordinates to a ~meters grid. Good for approximate storage.
 * @param {number} lat
 * @param {number} lng
 * @param {number} gridMeters default 100
 * @returns {{lat:number,lng:number}}
 */
export function snapToGrid(lat, lng, gridMeters = 100) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat, lng }
  const latStep = gridMeters / metersPerDegreeLat()
  const lngStep = gridMeters / metersPerDegreeLng(lat)

  const snappedLat = Math.round(lat / latStep) * latStep
  const snappedLng = Math.round(lng / lngStep) * lngStep

  // keep predictable decimal size for storage/comparisons
  return { lat: +snappedLat.toFixed(6), lng: +snappedLng.toFixed(6) }
}

/**
 * Round to N decimals (3 ≈ 110 m). Simpler alternative to grid snapping.
 * @param {number} lat
 * @param {number} lng
 * @param {number} decimals default 3
 * @returns {{lat:number,lng:number}}
 */
export function roundLatLng(lat, lng, decimals = 3) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat, lng }
  const f = Math.pow(10, decimals)
  return { lat: Math.round(lat * f) / f, lng: Math.round(lng * f) / f }
}

/**
 * One-shot helper: for 'approximate' returns quantized center; for 'exact' returns original.
 * @param {'exact'|'approximate'} addressType
 * @param {number} lat
 * @param {number} lng
 * @param {{strategy?:'grid'|'round', gridMeters?:number, decimals?:number}} [options]
 * @returns {{lat:number|null,lng:number|null}}
 */
export function quantizeLatLngForApprox(addressType, lat, lng, options = {}) {
  const isExact = addressType === 'exact'
  const latNum = Number(lat)
  const lngNum = Number(lng)
  const finite = Number.isFinite(latNum) && Number.isFinite(lngNum)
  if (!finite) return { lat: null, lng: null }
  if (isExact) return { lat: latNum, lng: lngNum }

  const strategy = options.strategy || 'grid'
  if (strategy === 'round') {
    const dec = Number.isFinite(options.decimals) ? options.decimals : 3
    return roundLatLng(latNum, lngNum, dec)
  }
  const gm = Number.isFinite(options.gridMeters) ? options.gridMeters : 100
  return snapToGrid(latNum, lngNum, gm)
}
/** Safe number coercion + ESLint-friendly finiteness checks */
export function toNumber(v) { return typeof v === 'number' ? v : Number(v) }
export function isFiniteNumber(v) { return Number.isFinite(toNumber(v)) }
export function round6(v) { return Math.round(toNumber(v) * 1e6) / 1e6 }

/**
 * Build a Leaflet-like viewport from center+radius in METERS
 * Uses the existing computeViewport (expects KM) + toLeafletBounds.
 */
export function viewportFromCenterMeters(lat, lng, radiusMeters) {
  if (!isFiniteNumber(lat) || !isFiniteNumber(lng) || !isFiniteNumber(radiusMeters) || radiusMeters <= 0) {
    return {}
  }
  const km = toNumber(radiusMeters) / 1000
  const bbox = computeViewport(toNumber(lat), toNumber(lng), km)
  const norm = toLeafletBounds({
    south_lat: bbox.south_lat,
    west_lng: bbox.west_lng,
    north_lat: bbox.north_lat,
    east_lng: bbox.east_lng,
  })
  if (norm && norm._southWest && norm._northEast) return norm
  return {}
}

/**
 * Decide the viewport for a point:
 * - If point_address_type === 'exact' → {}
 * - Else prefer storeLocation.viewport (any shape supported by toLeafletBounds)
 * - Else derive from pointData.lat/lng + pointData.location_radius (meters)
 * - Else normalize any existing pointData.viewport
 * Returns {} or a Leaflet-like {_southWest,{lat,lng}}, {_northEast,{lat,lng}}, rounded to 6 decimals.
 */
export function resolveViewportForPoint(pointData, storeLocation) {
  const pd = pointData || {}
  const type = String(pd.point_address_type || '').toLowerCase()
  if (type === 'exact') return {}

  // 1) prefer store location viewport
  const loc = storeLocation || null
  if (loc && loc.viewport) {
    const fromStore = toLeafletBounds(loc.viewport)
    if (fromStore && fromStore._southWest && fromStore._northEast) {
      return {
        _southWest: { lat: round6(fromStore._southWest.lat), lng: round6(fromStore._southWest.lng) },
        _northEast: { lat: round6(fromStore._northEast.lat), lng: round6(fromStore._northEast.lng) },
      }
    }
  }

  // 2) derive from center + radius (meters)
  const lat = toNumber(pd.lat)
  const lng = toNumber(pd.lng)
  const r = toNumber(pd.location_radius)
  if (isFiniteNumber(lat) && isFiniteNumber(lng) && isFiniteNumber(r) && r > 0) {
    const fromCenter = viewportFromCenterMeters(lat, lng, r)
    if (fromCenter._southWest && fromCenter._northEast) {
      return {
        _southWest: { lat: round6(fromCenter._southWest.lat), lng: round6(fromCenter._southWest.lng) },
        _northEast: { lat: round6(fromCenter._northEast.lat), lng: round6(fromCenter._northEast.lng) },
      }
    }
  }

  // 3) fallback: normalize any existing pointData.viewport
  if (pd.viewport) {
    const normPd = toLeafletBounds(pd.viewport)
    if (normPd && normPd._southWest && normPd._northEast) {
      return {
        _southWest: { lat: round6(normPd._southWest.lat), lng: round6(normPd._southWest.lng) },
        _northEast: { lat: round6(normPd._northEast.lat), lng: round6(normPd._northEast.lng) },
      }
    }
  }

  return {}
}

/** Ensure a Leaflet-like object or {} (never null/undefined) */
export function ensureViewportObject(vp) {
  const norm = toLeafletBounds(vp || {})
  if (norm && norm._southWest && norm._northEast) {
    return {
      _southWest: { lat: round6(norm._southWest.lat), lng: round6(norm._southWest.lng) },
      _northEast: { lat: round6(norm._northEast.lat), lng: round6(norm._northEast.lng) },
    }
  }
  return {}
}
// --- helpers to avoid "reading 'lat' of undefined" ---
export function isLeafletLike(vp) {
  return !!(
    vp
    && vp._southWest && vp._northEast
    && Number.isFinite(Number(vp._southWest.lat))
    && Number.isFinite(Number(vp._southWest.lng))
    && Number.isFinite(Number(vp._northEast.lat))
    && Number.isFinite(Number(vp._northEast.lng))
  )
}
export function sameViewport(a, b) {
  const A = isLeafletLike(a)
  const B = isLeafletLike(b)
  if (!A && !B) return true
  if (!A || !B) return false
  const n = Number
  return (
    n(a._southWest.lat) === n(b._southWest.lat)
    && n(a._southWest.lng) === n(b._southWest.lng)
    && n(a._northEast.lat) === n(b._northEast.lat)
    && n(a._northEast.lng) === n(b._northEast.lng)
  )
}

/** Wait until the page is focused/visible again (e.g., after Android accuracy sheet). */
function waitForFocusOrVisibility(maxMs = 20000) {
  return new Promise(resolve => {
    let done = false
    let timer
    const finish = () => {
      if (!done) {
        done = true
        window.removeEventListener('focus', onFocus, true)
        document.removeEventListener('visibilitychange', onVis, true)
        if (timer) clearTimeout(timer)
        resolve()
      }
    }
    const onFocus = () => finish()
    const onVis = () => {
      if (document.visibilityState === 'visible') finish()
    }

    window.addEventListener('focus', onFocus, true)
    document.addEventListener('visibilitychange', onVis, true)

    // if already visible, resolve on next tick
    if (document.visibilityState === 'visible') setTimeout(finish, 0)

    timer = setTimeout(finish, maxMs)
  })
}

/** Sometimes Android delivers the first fix faster via watchPosition. */
function warmUpWithWatchPosition(timeoutMs = 8000, enableHighAccuracy = false) {
  if (!navigator.geolocation) {
    return Promise.reject(Object.assign(new Error(i18n.t('location.errors.GeolocationUnsupported')), { code: -1 }))
  }
  return new Promise((resolve, reject) => {
    const id = navigator.geolocation.watchPosition(
      pos => { try { navigator.geolocation.clearWatch(id) } catch (_) {} resolve(pos.coords) },
      err => { try { navigator.geolocation.clearWatch(id) } catch (_) {} reject(err) },
      { enableHighAccuracy, timeout: timeoutMs, maximumAge: 0 },
    )
    setTimeout(() => { try { navigator.geolocation.clearWatch(id) } catch (_) {} }, timeoutMs + 1000)
  })
}
// ---- Location creation helpers (shared between AddLocationWizard & LocationBlock) ----

/**
 * Extract the "location" object from various backend response shapes.
 * Works with:
 *   - axios { data: { data: {...} } }
 *   - axios { data: {...} }
 *   - { location: {...} }
 *   - as a last resort, store.state.location.location
 */
export function extractLocationFromResponse(resp) {
  return (
    resp?.data?.data
    || resp?.data
    || resp?.location
    || (store && store.state && store.state.location && store.state.location.location)
    || null
  )
}

/**
 * Try to extract the newly created location id from response.
 * Looks for:
 *   - loc.id / loc.location_id / loc.uuid
 *   - Location header ending with /<id>
 */
export function extractLocationId(resp) {
  const loc = extractLocationFromResponse(resp)
  let id = loc?.id ?? loc?.location_id ?? loc?.uuid ?? null

  if (!id) {
    const locHeader = resp?.headers?.location || resp?.headers?.Location
    if (typeof locHeader === 'string') {
      const m = locHeader.match(/\/(\d+|[a-f0-9-]{8,})$/i)
      if (m) id = m[1]
    }
  }

  return id
}

/**
 * Build the payload for location/addLocation using *normalized* addresses.
 *
 * @param {Object} opts
 * @param {number} opts.lat                original latitude
 * @param {number} opts.lng                original longitude
 * @param {'exact'|'approximate'} opts.addressType
 * @param {Object} opts.normalizedExact    full cleaned address (mode:'exact')
 * @param {Object} opts.normalizedDisplay  privacy-safe subset (mode: addressType)
 * @param {number} opts.radiusMeters       requested radius in METERS
 * @param {number} [opts.radiusMin]        clamp min (default RADIUS_MIN)
 * @param {number} [opts.radiusMax]        clamp max (default RADIUS_MAX)
 * @param {string} [opts.comment]          optional address comment
 */
export function buildLocationApiPayload({
  lat,
  lng,
  addressType,
  normalizedExact,
  normalizedDisplay,
  radiusMeters,
  radiusMin = RADIUS_MIN,
  radiusMax = RADIUS_MAX,
  comment,
}) {
  // Quantize center only if approximate
  const { lat: storedLat, lng: storedLng } = quantizeLatLngForApprox(
    addressType,
    lat,
    lng,
    { strategy: 'grid', gridMeters: 100 }, // same logic as AddLocationWizard
  )

  const isExact = addressType === 'exact'

  const fullExact = normalizedExact || {}
  const approxOnly = normalizedDisplay || {}
  const addressPayload = isExact ? fullExact : approxOnly

  const clampedRadius = Math.round(
    Math.min(
      Math.max(Number(radiusMeters) || radiusMin, radiusMin),
      radiusMax,
    ),
  )

  return {
    data: {
      lat: storedLat,
      lng: storedLng,
    },
    address: {
      ...addressPayload,
      location_radius: isExact ? 0 : clampedRadius, // meters
      address_comment: comment || undefined,
      address_confirmed: isExact && !!(
        fullExact.house_number || approxOnly.house_number
      ),
    },
  }
}

/**
 * Build an addLocation payload starting from a *public location* object
 * (e.g. result of location/fetchPublicLocation).
 *
 */
export function buildLocationPayloadFromPublicLocation(raw) {
  if (!raw || typeof raw !== 'object') return null

  const lat = Number(raw.lat ?? raw.latitude)
  const lng = Number(raw.lng ?? raw.longitude)
  const address = raw.address || {}

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const radius = Number(raw.location_radius) || 0
  const isApprox = radius > 0

  return {
    data: {
      lat,
      lng,
    },
    address: {
      ...address,
      location_radius: isApprox ? radius : 0,
      address_comment: address.address_comment || undefined,
      address_confirmed: !!address.address_confirmed,
    },
  }
}

/**
 * Helper for emitting "extras" to parent components (like AddLocationWizard does).
 * Converts [[south,west],[north,east]] into Leaflet-like and returns a compact object.
 */
export function viewportArrayToObj(boundsArr) {
  if (!Array.isArray(boundsArr) || boundsArr.length !== 2) return null
  return {
    _southWest: { lat: boundsArr[0][0], lng: boundsArr[0][1] },
    _northEast: { lat: boundsArr[1][0], lng: boundsArr[1][1] },
  }
}

export function buildPointExtras({
  lat, lng, addressType, radiusMeters, viewportArray,
}) {
  const vpObj = viewportArrayToObj(viewportArray)

  return {
    lat,
    lng,
    exact: addressType === 'exact',
    radius: Number(radiusMeters) || 0,
    viewport: vpObj,
    viewportBounds: vpObj,
    viewportArray: viewportArray || [],
  }
}

export function kilometersToAwayLabel(km) {
  const value = Number(km)

  if (!Number.isFinite(value) || value <= 0) {
    return ''
  }

  let normalizedValue

  if (value < 10) {
    normalizedValue = Math.round(value * 10) / 10
  } else {
    normalizedValue = Math.round(value)
  }

  const displayValue = Number.isInteger(normalizedValue)
    ? String(normalizedValue)
    : normalizedValue.toFixed(1)

  return i18n.t('location.distance_km_away', { value: displayValue })
}
